import React, { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { UserProfileProvider, useUserProfile } from "../../contexts/UserProfileContext";
import {
  UserProfileLeftTabs,
  UserProfileRightTabs,
  UserProfileMobileMenu,
} from "../../components/profile/UserProfileTabs";
import UserProfileTabContent from "./UserProfileTabContent";
import PublicUserProfile from "./sections/PublicUserProfile";
import PrivateProfileView from "./PrivateProfileView";
import ProfileErrorBoundary from "../../components/common/ProfileErrorBoundary";
import styles from "../../styles/pages/user/UserProfilePage.module.css";
import publicUserProfileStyles from "../../styles/components/profile/PublicUserProfile.module.css";
import {
  FaCheckCircle,
  FaThumbsUp,
  FaHandsHelping,
  FaStar,
  FaClock,
} from "react-icons/fa";
import {
  getProfileTabFromPathname,
  PROFILE_SECTION_HASH,
  PROFILE_SECTION_ID,
} from "../../utils/profilePaths";

const UserProfileShell = () => {
  const location = useLocation();
  const { loading, profile, publicProfileProps, isOwnProfile, userId } =
    useUserProfile();

  const contributorLabels = useMemo(() => {
    if (!publicProfileProps) return [];
    const verifiedPurchases = Number(publicProfileProps.trustIndicators?.verifiedPurchases || 0);
    const helpfulRate = Number(publicProfileProps.trustIndicators?.helpfulRate || 0);
    const yearsActive = Number(publicProfileProps.trustIndicators?.yearsActive || 0);
    const reviewsWritten = Number(
      publicProfileProps.impact?.reviewsWritten ?? publicProfileProps.stats?.reviewsWritten ?? 0,
    );
    const helpfulVotesReceived = Number(
      publicProfileProps.impact?.helpfulVotesReceived ?? publicProfileProps.stats?.helpfulVotesReceived ?? 0,
    );
    const usersHelped = Number(publicProfileProps.impact?.usersHelped ?? 0);

    const labels = [];

    if (verifiedPurchases > 0) {
      labels.push({
        key: "verified-buyer",
        text: "Verified Buyer",
        description: "Has verified purchase reviews",
        icon: FaCheckCircle,
        priority: 1,
      });
    }

    if (helpfulVotesReceived >= 1) {
      labels.push({
        key: "helpful-reviewer",
        text: "Helpful Reviewer",
        description: "Highly impactful reviews",
        icon: FaThumbsUp,
        priority: 2,
      });
    }

    if (usersHelped >= 1) {
      labels.push({
        key: "community-helper",
        text: "Community Helper",
        description: "Broad community impact",
        icon: FaHandsHelping,
        priority: 3,
      });
    }

    if (reviewsWritten >= 1) {
      labels.push({
        key: "active-contributor",
        text: "Active Contributor",
        description: "Consistent review contributions",
        icon: FaStar,
        priority: 4,
      });
    }

    if (yearsActive >= 1 && reviewsWritten >= 1) {
      labels.push({
        key: "long-term",
        text: "Long-term Contributor",
        description: "Active across multiple years",
        icon: FaClock,
        priority: 5,
      });
    }

    return labels.sort((a, b) => a.priority - b.priority).slice(0, 3);
  }, [publicProfileProps]);

  useEffect(() => {
    if (loading || !profile) return;

    const activeTab = getProfileTabFromPathname(location.pathname, userId);
    const shouldScrollToSection =
      location.hash === PROFILE_SECTION_HASH || activeTab !== "posts";

    if (!shouldScrollToSection) return;

    const timer = window.setTimeout(() => {
      document.getElementById(PROFILE_SECTION_ID)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);

    return () => window.clearTimeout(timer);
  }, [location.pathname, location.hash, userId, loading, profile]);

  if (loading && !profile) {
    return (
      <div className={`pageContainer ${styles.pageWrapper}`}>
        <div className="innerContainer">
          <div className={styles.loading}>Loading profile...</div>
        </div>
      </div>
    );
  }

  if (!profile || !publicProfileProps) {
    return (
      <div className={`pageContainer ${styles.pageWrapper}`}>
        <div className="innerContainer">
          <div className={styles.error}>User not found</div>
        </div>
      </div>
    );
  }

  const { isPrivate, isFollowing } = publicProfileProps;
  const shouldShowPrivate = isPrivate && !isFollowing && !isOwnProfile;

  if (shouldShowPrivate) {
    return (
      <div className={`pageContainer ${styles.pageWrapper}`}>
        <div className={`innerContainer ${styles.profileInner}`}>
          <PrivateProfileView {...publicProfileProps} />
        </div>
      </div>
    );
  }

  return (
    <div className={`pageContainer ${styles.pageWrapper}`}>
      <div className={`innerContainer ${styles.profileInner}`}>
        <PublicUserProfile {...publicProfileProps} activeSection="header" />
        {(publicProfileProps?.badges?.length > 0 || contributorLabels.length > 0) && (
          <div className={publicUserProfileStyles.achievementSection} aria-label="Achievements">
            <span className={publicUserProfileStyles.achievementStripTitle}>Achievements</span>
            <div className={publicUserProfileStyles.badgeGrid}>
              {publicProfileProps.badges?.map((badge) => (
                <div
                  key={badge.id || badge.name}
                  className={`${publicUserProfileStyles.badgeCard} ${badge.tier ? publicUserProfileStyles[`badge_${badge.tier}`] : ""}`}
                  title={badge.description}
                >
                  <span className={publicUserProfileStyles.badgeIcon}>{badge.icon}</span>
                  <span className={publicUserProfileStyles.badgeName}>{badge.name}</span>
                  {badge.tier && (
                    <span className={publicUserProfileStyles.badgeTier}>{badge.tier}</span>
                  )}
                </div>
              ))}

              {contributorLabels.map((label) => {
                const Icon = label.icon;
                return (
                  <div
                    key={label.key}
                    className={`${publicUserProfileStyles.badgeCard} ${publicUserProfileStyles.contributorBadgeCard}`}
                    title={label.description}
                  >
                    <Icon className={publicUserProfileStyles.contributorBadgeIcon} aria-hidden />
                    <span className={publicUserProfileStyles.badgeName}>{label.text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <UserProfileMobileMenu />
        <div
          className={`${styles.profileBody} ${
            isOwnProfile ? styles.profileBodyWithRightSidebar : ""
          }`.trim()}
        >
          <UserProfileLeftTabs />
          <main
            id={PROFILE_SECTION_ID}
            className={styles.tabContent}
            aria-label="Profile section"
          >
            <ProfileErrorBoundary>
              <UserProfileTabContent />
            </ProfileErrorBoundary>
          </main>
          {isOwnProfile && <UserProfileRightTabs />}
        </div>
      </div>
    </div>
  );
};

const UserProfileLayout = () => (
  <UserProfileProvider>
    <UserProfileShell />
  </UserProfileProvider>
);

export default UserProfileLayout;
